import { Component, computed, inject, model, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { DateTime } from 'luxon';
import { linkedQueryParam, paramToNumber } from 'ngxtension/linked-query-param';
import { tap } from 'rxjs/operators';
import { LocationService } from '../../../service/location.service';
import { LocationSnapshotWithImage, MarketSnapshotsService } from '../../../service/market-snapshots.service';
import {
  Branch,
  CAMERA_SNAPSHOT_STATUS_OPTIONS,
  CameraSnapshotStatusOption,
  LocationBaseResponse,
  LocationTiny,
  RhombusCameraSnapshotStatus,
} from '../../../types';
import { cameraErrorMessage, embedSnapshotMetadata, sortByAttention } from './market-snapshots.utils';

@Component({
  selector: 'app-market-snapshots',
  templateUrl: './market-snapshots.component.html',
  styleUrls: ['./market-snapshots.component.scss'],
  standalone: false,
})
export class MarketSnapshotsComponent implements OnInit {
  private locationService = inject(LocationService);
  private marketSnapshotsService = inject(MarketSnapshotsService);
  private router = inject(Router);

  readonly pageBranchId = linkedQueryParam<number | null>('branch_id', {
    parse: paramToNumber(),
  });

  ngOnInit(): void {
    this.loadAllLocations();

    const branchIdFromQuery = this.pageBranchId();

    if (branchIdFromQuery) {
      this.selectedBranch.set({ id: branchIdFromQuery } as Branch);
    }
  }

  readonly pageLocationIds = linkedQueryParam<number[]>('location_ids', {
    parse: (value) => (value ? value.split(',').map((v) => parseInt(v, 10)) : []),
    stringify: (value) => value.join(','),
  });

  public selectedBranch = model<Branch | undefined>(undefined);
  public selectedLocations = model<LocationTiny[]>([]);

  public snapshotStatusOptions: CameraSnapshotStatusOption[] = CAMERA_SNAPSHOT_STATUS_OPTIONS;
  public selectedSnapshotStatuses = model<RhombusCameraSnapshotStatus[]>([]);
  public snapshotDate = linkedQueryParam<Date>('date', {
    parse: (value) =>
      value ? DateTime.fromFormat(value, 'yyyy-MM-dd').toJSDate() : DateTime.local().minus({ days: 1 }).startOf('day').toJSDate(),
    stringify: (value) => DateTime.fromJSDate(value).toFormat('yyyy-MM-dd'),
  });

  public isLoadingSnapshots = signal<boolean>(false);
  public locationOptions = signal<LocationTiny[]>([]);
  public allLocations = signal<LocationBaseResponse[]>([]);
  public snapshots = signal<LocationSnapshotWithImage[]>([]);
  private imagesLoadState = signal(0);

  public sortedSnapshots = computed(() => sortByAttention(this.snapshots()));

  public filteredSnapshots = computed(() => {
    const selectedStatuses = this.selectedSnapshotStatuses() ?? [];
    const selectedLocationIds = (this.selectedLocations() ?? []).map((l) => l.id);

    return this.sortedSnapshots().filter((s) => {
      const matchesStatus = selectedStatuses.length === 0 || (s.status != null && selectedStatuses.includes(s.status));

      const matchesLocation = selectedLocationIds.length === 0 || selectedLocationIds.includes(s.locationId);

      return matchesStatus && matchesLocation;
    });
  });

  public pageSubheader = computed(() => {
    const total = this.filteredSnapshots().length;
    return `Loaded ${total}/${total} locations`;
  });

  public emptyMessage = computed(() => {
    if (!this.selectedBranch()?.id) return 'Select a branch to view market snapshots';
    if (this.locationOptions().length === 0) return 'No locations have cameras or are not tied to a plan';
    if (this.filteredSnapshots().length === 0) return 'No snapshots are available.';
    return null;
  });

  public totalImageCount = computed(() => {
    this.imagesLoadState();
    return this.filteredSnapshots().reduce((sum, s) => sum + s.images.filter((i) => i.blob != null).length, 0);
  });

  public readonly cameraErrorMessage = cameraErrorMessage;

  public previewImage$(snapshot: LocationSnapshotWithImage) {
    return snapshot.images[0]?.image$ ?? null;
  }

  private filterLocationsByBranch(branchId: number | null) {
    const all = this.allLocations();

    if (!all || all.length === 0) {
      this.locationOptions.set([]);
      return;
    }

    const filtered = branchId == null ? all : all.filter((loc) => loc.branch_id === branchId);
    this.locationOptions.set(
      filtered.map((d) => ({
        id: d.id,
        name: d.name,
      })),
    );
  }

  loadAllLocations() {
    this.locationService
      .getAllLocations({
        has_plan: true,
        market_snapshots_enabled: true,
      })
      .subscribe((res) => {
        this.allLocations.set(res.items);
        this.filterLocationsByBranch(this.selectedBranch()?.id ?? null);

        const locationIdsFromQuery = this.pageLocationIds();
        this.selectedLocations.set(this.locationOptions().filter((l) => locationIdsFromQuery.includes(l.id)));
      });
  }

  viewSnapshot(snapshot: LocationSnapshotWithImage) {
    const url = this.router
      .createUrlTree(['/home', 'manage', 'branch', snapshot.branchId, 'location', snapshot.locationId], {
        queryParams: {
          subpage: 'market-snapshot',
          date: DateTime.fromJSDate(this.snapshotDate()).toISODate(),
        },
      })
      .toString();
    window.open(url, '_blank');
  }

  goToInstallerDashboard(snapshot: LocationSnapshotWithImage) {
    const url = this.router
      .createUrlTree(['/home', 'manage', 'branch', snapshot.branchId], {
        queryParams: { subpage: 'installer-dashboard' },
      })
      .toString();
    window.open(url, '_blank');
  }

  onBranchChange() {
    const branchId = this.selectedBranch()?.id ?? null;
    const isBranchChange = branchId !== this.pageBranchId();
    this.pageBranchId.set(branchId);

    if (isBranchChange) {
      this.selectedLocations.set([]);
      this.pageLocationIds.set([]);
    }
    this.snapshots.set([]);

    this.filterLocationsByBranch(branchId);

    if (branchId != null) {
      this.loadMarketSnapshots();
    }
  }

  onLocationsChange() {
    this.pageLocationIds.set(this.selectedLocations().map((l) => l.id));
  }

  loadMarketSnapshots() {
    const branchId = this.selectedBranch()?.id ?? null;

    if (!branchId) {
      this.snapshots.set([]);
      this.isLoadingSnapshots.set(false);
      return;
    }

    this.isLoadingSnapshots.set(true);
    this.snapshots.set([]);
    this.imagesLoadState.set(0);

    this.marketSnapshotsService.getBranchLocationSnapshots(branchId, [], this.snapshotDate()).subscribe({
      next: (locationSnapshots) => {
        locationSnapshots.forEach((locationSnapshot) => {
          locationSnapshot.images.forEach((image) => {
            image.image$ = image.image$.pipe(
              tap((result) => {
                if (result.value) {
                  this.imagesLoadState.update((n) => n + 1);
                }
              }),
            );
            image.image$.subscribe();
          });

          this.snapshots.update((current) => [...current, locationSnapshot]);
        });

        this.isLoadingSnapshots.set(false);
      },
      error: () => {
        this.isLoadingSnapshots.set(false);
      },
    });
  }

  private formatFileName(fileName: string): string {
    return fileName
      .normalize('NFKD')
      .replace(/[\\/:*?"<>|]/g, '')
      .replace(/[^\w\s.-]/g, '')
      .replace(/\s+/g, '_')
      .trim();
  }

  async exportAvailableImages() {
    const zip = new JSZip();
    const currentSnapshots = this.filteredSnapshots();

    for (const location of currentSnapshots) {
      for (const image of location.images) {
        if (!image.blob) continue;
        const formattedDate = DateTime.fromISO(image.requestDateTime).toFormat('yyyy-LL-dd_HH-mm-ss');
        const fileName = `${location.locationName}_${formattedDate}_${image.cameraName}_${image.status}.jpg`;
        const exportImageBlob = await embedSnapshotMetadata(image.blob, location.locationName, image.cameraName, image.status);
        zip.file(this.formatFileName(fileName), exportImageBlob);
      }
    }

    const zipFile = await zip.generateAsync({ type: 'blob' });
    const currentDate = DateTime.now().toISODate();

    saveAs(zipFile, `market-snapshots${this.selectedBranch() ? `-${this.selectedBranch()!.name}` : ''}-${currentDate}.zip`);
  }
}
